<template>
  <Modal :hide-close="true">
    <button
      type="button"
      aria-label="Close"
      class="absolute top-2 right-2 z-10 hidden size-[22px] cursor-pointer items-center justify-center rounded-[6px] text-gray-500/50 transition hover:bg-gray-200/25 hover:text-gray-500 tablet-v:flex"
      @click="handleClose"
    >
      <CloseIcon class="size-[13px]" aria-hidden="true" />
    </button>

    <Modal.Title>Create technical comparison analysis report</Modal.Title>

    <Modal.Body>
      <p class="text-[14px] leading-[22px] text-gray-800">
        Select works to compare for infringement with
        <DacrAIIcon
          class="inline-block h-[13px] w-auto align-[-1px]"
          aria-label="Dacr AI"
        />
      </p>

      <template v-if="uploading">
        <div class="mt-3.5 flex h-[240px] flex-col">
          <div class="flex flex-1 flex-col items-center justify-center">
            <UploadProgressRing :percentage="percentage" />
            <div
              v-truncate
              class="mt-[18px] w-full px-6 text-center text-[14px] leading-[20px] text-gray-800"
            >
              <div data-truncate-target>Uploading {{ file?.name }}</div>
            </div>
          </div>
          <Alert variant="info" :icon="AlertIcon">
            Keep your browser open until the upload is complete
          </Alert>
        </div>
      </template>

      <template v-else>
        <div class="mt-4 text-[12px] leading-[16px] text-gray-500">
          Your registered work on Dacr
        </div>

        <div
          v-if="copyright"
          class="mt-2 flex items-center gap-x-2.5 rounded-xl border border-gray-200 p-2"
        >
          <div class="size-14 shrink-0">
            <Thumbnail
              :type="fileTypeToThumbnail(copyright.type)"
              :src="copyrightThumb"
            />
          </div>
          <div
            v-truncate
            class="min-w-0 flex-1 text-[14px] leading-[22px] text-gray-800"
            :key="copyright.id"
          >
            <div data-truncate-target>{{ copyright.name }}</div>
          </div>
          <button
            type="button"
            class="text-link shrink-0"
            @click="handleSelect"
          >
            Change
          </button>
        </div>

        <Button
          v-else
          variant="stroke"
          class="mt-2 w-full"
          @click="handleSelect"
        >
          Select registered work
        </Button>

        <div
          class="mx-auto my-4 flex size-7 items-center justify-center rounded-full border border-black/8 bg-white shadow-[0_4px_18px_0_rgba(0,0,0,0.04)]"
        >
          <SortIcon class="size-5 text-gray-800" />
        </div>

        <div class="text-[12px] leading-[16px] text-gray-500">
          Potential infringing work
        </div>

        <input
          ref="fileInput"
          type="file"
          class="hidden"
          @change="handleInput"
        />

        <div
          v-if="error"
          class="mt-2 flex h-[200px] flex-col items-center justify-center rounded-xl border-2 border-dashed border-red-200 bg-red-200/5 px-6 text-center"
        >
          <div
            class="mb-3.5 flex size-10 items-center justify-center rounded-full bg-red-100"
          >
            <AlertIcon class="size-5 text-red-500" />
          </div>
          <p class="text-[14px] leading-[20px] text-gray-800">
            Upload failed. Please try again.<br />Your report credit was not
            used.
          </p>
          <button
            type="button"
            class="text-link mt-3 text-red-500!"
            @click="handleCreate"
          >
            Try again
          </button>
        </div>

        <div
          v-else-if="file"
          class="mt-2 flex h-[200px] flex-col items-center justify-center rounded-xl border border-gray-200 px-6 text-center"
        >
          <div class="mb-2.5 size-[90px]">
            <Thumbnail :type="pickedType" />
          </div>
          <div v-truncate class="mb-2.5 w-full text-[14px] text-gray-800">
            <div data-truncate-target>{{ file.name }}</div>
          </div>
          <button type="button" class="text-link" @click="handleRemove">
            Remove
          </button>
        </div>

        <div
          v-else
          class="mt-2 flex h-[200px] cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-red-200 bg-red-200/5 px-6 text-center transition-colors"
          :class="dragging && 'bg-red-200/20'"
          @click="fileInput?.click()"
          @dragover.prevent="dragging = true"
          @dragleave="dragging = false"
          @drop.prevent="handleDrop"
        >
          <svg
            viewBox="0 0 20 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
            stroke-linejoin="round"
            stroke-linecap="round"
            class="mb-[18px] h-6 w-5 text-red-500"
            aria-hidden="true"
          >
            <path
              d="M3 1h9l6 6v15a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1Z"
            />
            <path d="M12 1v6h6" />
          </svg>
          <div class="text-[14px] leading-[20px] text-gray-800">
            <div>Drag and drop infringing file</div>
            <div>
              or
              <span class="text-red-500 font-medium">upload from computer</span>
            </div>
          </div>
          <p class="mt-[18px] text-[14px] leading-[18px] text-gray-400">
            Max 10GB
          </p>
        </div>
      </template>
    </Modal.Body>

    <div v-if="!uploading" class="mt-6">
      <template v-if="ready">
        <p class="text-[12px] leading-[18px] font-medium text-gray-600">
          Creating this report uses 1 report credit. Once created, the credit is
          used and isn't returned to your balance.
        </p>

        <p class="mt-2.5 mb-3.5 text-[12px] leading-[18px] text-gray-600">
          By selecting Create report, I confirm I'm acting in good faith and
          that I hold the rights to the original work. I understand Dacr will
          store the original and detected material to process this report, and
          that I'm responsible for the report I create. I agree to the
          <a class="text-link" href="/legal/terms" target="_blank">Dacr Terms</a
          >,
          <a class="text-link" href="/legal/privacy" target="_blank"
            >Privacy Policy</a
          >, and
          <a class="text-link" href="/legal/payments" target="_blank"
            >Payments Policy</a
          >.
        </p>
      </template>

      <Button
        variant="primary"
        size="lg"
        class="w-full"
        :disabled="!ready"
        :loading="uploading"
        @click="handleCreate"
      >
        Create report
      </Button>
    </div>
  </Modal>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { Modal, Button, Thumbnail, Alert } from '@dacr/library'
import { CloseIcon, SortIcon, AlertIcon, DacrAIIcon } from '@dacr/icons'

import { fileTypeToThumbnail } from '@/shared/utils/thumbnail'
import { useCreateReport } from '../useCreateReport'
import UploadProgressRing from './UploadProgressRing.vue'

const fileInput = ref<HTMLInputElement>()

const {
  file,
  copyright,
  dragging,
  uploading,
  error,
  percentage,
  ready,
  copyrightThumb,
  pickedType,
  handleSelect,
  handleDrop,
  handleInput,
  handleRemove,
  handleClose,
  handleCreate,
} = useCreateReport()
</script>
